import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Warehouse } from '@views/WarehouseDetails/types';
import { WAREHOUSE_SEARCH_URL } from '@constants';
import { useAuthHttp } from '@hooks';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { isPublicPageAtom } from '../../store/jotai';
import { Mapbox } from './Mapbox';

const UK_CENTER = { lat: 54.3781, lon: -2.436 };
const DEFAULT_ZOOM_NO_COORDS = 5;
const DEFAULT_ZOOM_WITH_COORDS = 10;

export function WarehouseSearchEmbed() {
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();
  const setIsPublicPage = useSetAtom(isPublicPageAtom);

  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const zoomParam = searchParams.get('zoom');

  const hasCoords = !!latParam && !!lngParam;
  const mapPosition = hasCoords
    ? { lat: Number(latParam), lon: Number(lngParam) }
    : UK_CENTER;
  const zoomLevel = zoomParam
    ? Number(zoomParam)
    : hasCoords
      ? DEFAULT_ZOOM_WITH_COORDS
      : DEFAULT_ZOOM_NO_COORDS;

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setIsPublicPage(true);
  }, [setIsPublicPage]);

  useEffect(() => {
    let cancelled = false;
    const fetchWarehouses = async () => {
      setIsFetching(true);
      try {
        const { data } = await authHttp.post(WAREHOUSE_SEARCH_URL, {
          location: mapPosition,
          distanceUnit: 'km',
          radius: 250,
          pagination: { pageNumber: 0, itemsPerPage: 5000 },
        });
        if (!cancelled && data?.warehouses) {
          setWarehouses(data.warehouses);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('warehouse embed fetch error:', error);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };
    fetchWarehouses();
    return () => {
      cancelled = true;
    };
  }, [mapPosition.lat, mapPosition.lon]);

  return (
    <div className='relative h-screen w-screen overflow-hidden'>
      <Mapbox
        warehouses={warehouses}
        mapPosition={mapPosition}
        zoomLevel={zoomLevel}
        selectable={false}
      />
      {isFetching && (
        <div className='absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg shadow-md px-4 py-2 flex items-center gap-2'>
          <FloxSpinner size='sm' />
          <span className='text-sm text-gray-700'>Loading warehouses…</span>
        </div>
      )}
    </div>
  );
}
